import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Upload, X, File, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import type { DocumentFolder } from '@/types/document';

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  folders: DocumentFolder[];
  onUploadSuccess?: () => void;
}

interface FileWithPreview {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
}

export function DocumentUploadDialog({
  open,
  onOpenChange,
  employeeId,
  folders,
  onUploadSuccess,
}: DocumentUploadDialogProps) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [newFolderName, setNewFolderName] = useState('');
  const [documentName, setDocumentName] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [useNewFolder, setUseNewFolder] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles: FileWithPreview[] = Array.from(e.dataTransfer.files).map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      }));
      setFiles((prev) => [...prev, ...droppedFiles]);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles: FileWithPreview[] = Array.from(e.target.files).map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
      }));
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const formatFileSize = (bytes: number | undefined | null): string => {
    if (!bytes || isNaN(bytes) || bytes < 0) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFolderSelect = (folderId: string) => {
    if (folderId === '__none__') {
      // Clear selection
      setSelectedFolderId('');
      setUseNewFolder(false);
      setNewFolderName('');
    } else {
      setSelectedFolderId(folderId);
      setUseNewFolder(false);
      setNewFolderName(''); // Clear new folder name when selecting existing folder
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (!documentName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();

      // Add files
      files.forEach((fileWithPreview) => {
        formData.append('files', fileWithPreview.file);
      });

      // Add document name (will override original file names)
      formData.append('documentName', documentName.trim());

      // Add folder info
      if (selectedFolderId) {
        // Use existing folder - find folder name by ID
        const folder = folders.find((f) => f.id === selectedFolderId);
        if (folder) {
          formData.append('folderName', folder.name);
        }
      } else if (useNewFolder && newFolderName.trim()) {
        // Create new folder
        formData.append('folderName', newFolderName.trim());
      }

      // Call API endpoint
      // Note: axios interceptor handles FormData Content-Type automatically
      await api.post(`/employees/${employeeId}/documents/upload`, formData);

      // Reset form
      setFiles([]);
      setSelectedFolderId('');
      setNewFolderName('');
      setDocumentName('');
      setUseNewFolder(false);
      onOpenChange(false);

      // Wait a bit before invalidating to ensure backend has processed
      setTimeout(() => {
        onUploadSuccess?.();
      }, 500);
    } catch {
      toast.error('Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setSelectedFolderId('');
      setNewFolderName('');
      setDocumentName('');
      setUseNewFolder(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            Upload one or more documents. Drag and drop files or click to select.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drag and Drop Area */}
          <div
            className={cn(
              'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
              dragActive
                ? 'border-primary bg-primary/5'
                : 'border-border bg-muted/30 hover:bg-muted/50',
              files.length > 0 && 'border-primary/50'
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />

            {files.length === 0 ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="rounded-full bg-primary/10 p-4">
                  <Upload className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Drag and drop files here
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    or click to browse
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer"
                >
                  Select Files
                </Button>
              </div>
            ) : (
              <div className="w-full space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">
                    {files.length} file{files.length !== 1 ? 's' : ''} selected
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-7 text-xs cursor-pointer"
                  >
                    Add More
                  </Button>
                </div>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 rounded-md border border-border bg-card p-2"
                    >
                      <File className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground truncate">
                          Original: {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 cursor-pointer"
                        onClick={() => removeFile(file.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Document Name */}
          <div className="space-y-2">
            <Label htmlFor="document-name">
              Document Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="document-name"
              placeholder="Enter document name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={isUploading}
              required
            />
            <p className="text-xs text-muted-foreground">
              This name will be used instead of the original file name
            </p>
          </div>

          {/* Folder Selection */}
          <div className="space-y-2">
            <Label htmlFor="folder">Folder</Label>
            <Select value={selectedFolderId || '__none__'} onValueChange={handleFolderSelect}>
              <SelectTrigger id="folder" className="w-full">
                <SelectValue placeholder="Select folder or create new" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (create new folder)</SelectItem>
                {folders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Create New Folder */}
          <div className="space-y-2">
            <Label htmlFor="new-folder">Or create new folder</Label>
            <Input
              id="new-folder"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => {
                if (selectedFolderId) return;
                setNewFolderName(e.target.value);
                if (e.target.value.trim()) {
                  setUseNewFolder(true);
                  setSelectedFolderId('');
                }
              }}
              disabled={!!selectedFolderId || isUploading}
            />
            {useNewFolder && newFolderName.trim() && !selectedFolderId && (
              <p className="text-xs text-muted-foreground">
                New folder &quot;{newFolderName.trim()}&quot; will be created
              </p>
            )}
            {selectedFolderId && (
              <p className="text-xs text-muted-foreground">
                Clear folder selection above to create a new folder
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={files.length === 0 || !documentName.trim() || isUploading}
            className="cursor-pointer"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload {files.length} file{files.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
